<template>
  <div class="group bg-white rounded-[24px] p-4 border border-[#1c4848]/5 shadow-sm hover:shadow-xl hover:border-[#1c4848]/10 transition-all duration-300 flex flex-col">
    <div class="relative aspect-[16/10] rounded-2xl overflow-hidden mb-4 bg-gray-100">
      <img
        :src="coverUrl"
        class="w-full h-full object-cover group-hover:scale-105 transition-transform duration-500"
        alt=""
        @error="($event.target).src = 'https://placehold.co/400x250/fafbfc/1c4848?text=Game'"
      >
      <div class="absolute top-3 left-3 flex gap-2">
        <span
          class="px-2 py-1 rounded-lg bg-white/90 backdrop-blur-sm text-[10px] font-bold uppercase tracking-wider shadow-sm flex items-center gap-1"
          :class="typeBadgeClass"
        >
          <component :is="typeIcon" class="w-3.5 h-3.5" />
          {{ typeLabel }}
        </span>
      </div>
      <div class="absolute inset-0 bg-[#1c4848]/60 opacity-0 group-hover:opacity-100 transition-opacity duration-300 flex items-center justify-center gap-2 flex-wrap">
        <button type="button" class="w-10 h-10 rounded-full bg-emerald-500 text-white flex items-center justify-center hover:scale-110 transition-transform shadow-lg" title="Publish to feed" @click="publishToFeed">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><path d="M12 19V5"/><path d="m5 12 7-7 7 7"/></svg>
        </button>
        <button type="button" class="w-10 h-10 rounded-full bg-[#ecd79f] text-[#1c4848] flex items-center justify-center hover:scale-110 transition-transform shadow-lg" title="Play" @click="$emit('play', game)">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="currentColor"><path d="M8 5v14l11-7z"/></svg>
        </button>
        <button
          v-if="game?.id"
          type="button"
          class="w-10 h-10 rounded-full bg-white/90 text-[#1c4848] flex items-center justify-center hover:scale-110 transition-transform shadow-lg disabled:opacity-50"
          title="Duplicate game"
          :disabled="duplicateLoading"
          @click.stop="openDuplicateConfirm"
        >
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2" stroke-linecap="round" stroke-linejoin="round"><rect width="14" height="14" x="8" y="8" rx="2" ry="2"/><path d="M4 16c-1.1 0-2-.9-2-2V4c0-1.1.9-2 2-2h10c1.1 0 2 .9 2 2"/></svg>
        </button>
        <button type="button" class="w-10 h-10 rounded-full bg-white text-[#1c4848] flex items-center justify-center hover:scale-110 transition-transform shadow-lg" title="Edit" @click="$emit('edit', game)">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M17 3a2.85 2.83 0 1 1 4 4L7.5 20.5 2 22l1.5-5.5Z"/><path d="m15 5 4 4"/></svg>
        </button>
        <button type="button" class="w-10 h-10 rounded-full bg-white text-red-500 flex items-center justify-center hover:scale-110 transition-transform shadow-lg" title="Delete" @click="$emit('delete', game)">
          <svg xmlns="http://www.w3.org/2000/svg" width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" stroke-width="2"><path d="M3 6h18"/><path d="M19 6v14c0 1-1 2-2 2H7c-1 0-2-1-2-2V6"/><path d="M8 6V4c0-1 1-2 2-2h4c1 0 2 1 2 2v2"/><line x1="10" y1="11" x2="10" y2="17"/><line x1="14" y1="11" x2="14" y2="17"/></svg>
        </button>
      </div>
    </div>

    <ConfirmDialog
      v-model="showDuplicateConfirm"
      title="Kopiraj igru"
      :message="duplicateConfirmMessage"
      confirm-label="Kopiraj"
      cancel-label="Odustani"
      @confirm="confirmDuplicate"
    />

    <div class="flex justify-between items-start mb-2">
      <h3 class="font-serif text-lg font-bold text-[#1c4848] leading-tight">
        {{ game.name || '—' }}
      </h3>
      <div v-if="game.rating != null" class="flex items-center gap-0.5 text-[#ecd79f]">
        <svg xmlns="http://www.w3.org/2000/svg" width="12" height="12" viewBox="0 0 24 24" fill="currentColor"><path d="M12 2l3.09 6.26L22 9.27l-5 4.87 1.18 6.88L12 17.77l-6.18 3.25L7 14.14 2 9.27l6.91-1.01L12 2z"/></svg>
        <span class="text-xs font-bold text-[#1c4848]">{{ Number(game.rating).toFixed(1) }}</span>
      </div>
    </div>

    <p class="text-xs text-[#1c4848]/50 line-clamp-2 mb-4">
      {{ game.description || 'No description.' }}
    </p>
    <div class="mt-auto flex items-center justify-between pt-3 border-t border-[#1c4848]/5">
      <div class="flex items-center gap-2">
        <span class="px-2.5 py-1 rounded-md text-[10px] font-bold uppercase tracking-wide" :class="difficultyBadgeClass">
          {{ difficultyLabel }}
        </span>
        <span v-if="gridLabel" class="text-[10px] font-bold text-[#1c4848]/40 bg-gray-50 px-2 py-1 rounded-md border border-gray-100">
          {{ gridLabel }}
        </span>
      </div>
    </div>
  </div>
</template>

<script setup>
import { computed, ref } from 'vue';
import IconMemoryGame from '@/components/app/dashboard/games/icons/IconMemoryGame.vue';
import IconSlidingPuzzle from '@/components/app/dashboard/games/icons/IconSlidingPuzzle.vue';
import IconCatchStar from '@/components/app/dashboard/games/icons/IconCatchStar.vue';
import IconColorMatch from '@/components/app/dashboard/games/icons/IconColorMatch.vue';
import IconBubbleShooter from '@/components/app/dashboard/games/icons/IconBubbleShooter.vue';
import IconBlockStack from '@/components/app/dashboard/games/icons/IconBlockStack.vue';
import { useContentFeedStore } from '@/store/content-feed';
import { useGamesStore } from '@/store/games';
import { getContentFeedType, getContentLanguageId } from '@/utils/contentFeedType.js';
import ConfirmDialog from '@/components/app/dashboard/modals/ConfirmDialog.vue';

const props = defineProps({
  game: { type: Object, required: true },
});

const emit = defineEmits(['play', 'edit', 'delete', 'duplicated']);

const contentFeedStore = useContentFeedStore();
const gamesStore = useGamesStore();

const showDuplicateConfirm = ref(false);
const duplicateLoading = ref(false);

const duplicateConfirmMessage = computed(() => {
  const n = props.game?.name || '—';
  return `Da li želite da napravite kopiju igre „${n}"? Nova igra će dobiti ime sa oznakom (kopija).`;
});

function openDuplicateConfirm() {
  if (!props.game?.id || duplicateLoading.value) return;
  showDuplicateConfirm.value = true;
}

async function confirmDuplicate() {
  const game = props.game;
  if (!game?.id) return;
  duplicateLoading.value = true;
  try {
    await gamesStore.duplicateGame(game.id, {});
    showDuplicateConfirm.value = false;
    emit('duplicated', game);
  } catch (e) {
    console.error(e);
  } finally {
    duplicateLoading.value = false;
  }
}

function publishToFeed() {
  const game = props.game;
  if (!game?.id) return;
  if (!confirm('Publish to feed?')) return;
  const title = game?.name ?? 'Game';
  const type = getContentFeedType(game, 'game');
  const languageId = getContentLanguageId(game);
  contentFeedStore.createContentFeed({ title, type, language_id: languageId }).then((res) => {
    gamesStore.attachContentFeed(game.id, res.id);
  });
}

const coverUrl = computed(() => props.game.cover?.url || '');

const typeLabel = computed(() => {
  const t = props.game.game_type;
  if (t === 'match_pairs') return 'Memory';
  if (t === 'sliding_puzzle') return 'Puzzle';
  if (t === 'catch_star') return 'Catch Star';
  if (t === 'color_match') return 'Color Match';
  if (t === 'bubble_shooter') return 'Bubble Shooter';
  if (t === 'block_stack') return 'Stack';
  return t || '—';
});

const typeBadgeClass = computed(() => {
  const t = props.game.game_type;
  if (t === 'match_pairs') return 'text-[#00695c]';
  if (t === 'sliding_puzzle') return 'text-[#e65100]';
  if (t === 'catch_star') return 'text-[#f9a825]';
  if (t === 'color_match') return 'text-[#43a047]';
  if (t === 'bubble_shooter') return 'text-[#1e88e5]';
  if (t === 'block_stack') return 'text-[#795548]';
  return 'text-[#1c4848]/60';
});

const typeIcon = computed(() => {
  const t = props.game.game_type;
  if (t === 'sliding_puzzle') return IconSlidingPuzzle;
  if (t === 'catch_star') return IconCatchStar;
  if (t === 'color_match') return IconColorMatch;
  if (t === 'bubble_shooter') return IconBubbleShooter;
  if (t === 'block_stack') return IconBlockStack;
  return IconMemoryGame;
});

const difficultyLabel = computed(() => {
  const d = props.game.difficulty;
  if (d == null) return '—';
  if (d <= 1) return 'Easy';
  if (d <= 3) return 'Medium';
  return 'Hard';
});

const difficultyBadgeClass = computed(() => {
  const d = props.game.difficulty;
  if (d <= 1) return 'badge-easy';
  if (d <= 3) return 'badge-medium';
  return 'badge-hard';
});

const gridLabel = computed(() => {
  const c = props.game.config;
  if (!c) return '';
  const g = c.grid_size || c.gridSize;
  if (g) return `${g} Grid`;
  if (c.grid_cols != null && c.grid_rows != null) return `${c.grid_cols}×${c.grid_rows}`;
  if (c.grid_cols) return `${c.grid_cols} cols`;
  if (c.lives != null && c.time_limit != null) return `${c.lives} lives · ${c.time_limit}s`;
  return '';
});
</script>

<style scoped>
.badge-easy { background: #e1f5e8; color: #00695c; border: 1px solid rgba(0, 105, 92, 0.1); }
.badge-medium { background: #fff8e1; color: #f57f17; border: 1px solid rgba(245, 127, 23, 0.1); }
.badge-hard { background: #ffebee; color: #c62828; border: 1px solid rgba(198, 40, 40, 0.1); }
</style>
